import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_URL } from "../api";
import { useAuth } from "../AuthContext";
import StatusBadge from "../components/StatusBadge";

const CATEGORIES = ["", "ski", "boots", "poles", "binding", "lurk"];
const CATEGORY_LABELS = {
  "": "Alle",
  ski: "Ski",
  boots: "Schuhe",
  poles: "Stöcke",
  binding: "Bindung",
  lurk: "Lurk",
};
const STATUSES = ["", "available", "rented", "maintenance", "retired"];

export default function Items() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: "", status: "" });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filter.category) params.category = filter.category;
    if (filter.status) params.status = filter.status;
    api.listItems(params).then(setItems).finally(() => setLoading(false));
  }, [filter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => {
      const eq = i.equipment;
      const hay = [i.id, eq?.brand, eq?.model, eq?.size, i.serial_number, i.owner]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const printSheet = async () => {
    if (selected.size === 0) return;
    const res = await fetch(`${API_URL}/items/qr-sheet`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(Array.from(selected)),
    });
    if (!res.ok) {
      alert("Druckbogen konnte nicht erstellt werden");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl">Inventar</h1>
          <p className="text-fh-gray text-sm mt-1">{filtered.length} Items</p>
        </div>
        {isAdmin && selected.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-fh-gray">{selected.size} ausgewählt</span>
            <button onClick={printSheet} className="btn-primary">
              🖨️ QR-Bogen drucken
            </button>
            <button onClick={() => setSelected(new Set())} className="btn-ghost">Auswahl löschen</button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <select
          className="input-field max-w-[180px]"
          value={filter.category}
          onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))}
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
          ))}
        </select>
        <select
          className="input-field max-w-[180px]"
          value={filter.status}
          onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s ? statusDe(s) : "Alle Status"}</option>
          ))}
        </select>
        <input
          className="input-field flex-1 max-w-md"
          type="search"
          placeholder="Suche nach ID, Marke, Modell, Seriennummer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-fh-gray">Lade…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-fh-gray">Keine Items gefunden.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((i) => {
              const eq = i.equipment;
              return (
                <li key={i.id} className="px-4 py-3 flex items-center gap-3">
                  {isAdmin && (
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-fh-red"
                      checked={selected.has(i.id)}
                      onChange={() => toggle(i.id)}
                    />
                  )}
                  <Link to={`/items/${i.id}`} className="flex-1 min-w-0 flex items-center gap-3 hover:bg-fh-gray-light -mx-2 px-2 py-1 rounded">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{i.id}</span>
                        <StatusBadge status={i.status} />
                        {i.owner && i.owner !== "freeheeler" && (
                          <span className="badge bg-blue-50 text-blue-800">{i.owner}</span>
                        )}
                      </div>
                      <div className="text-xs text-fh-gray truncate mt-0.5">
                        {[CATEGORY_LABELS[eq?.category] || eq?.category, eq?.brand, eq?.model, eq?.size]
                          .filter(Boolean).join(" · ")}
                        {i.home_location?.name && ` · 📍 ${i.home_location.name}`}
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function statusDe(s) {
  return { available: "Verfügbar", rented: "Verliehen", maintenance: "Wartung", retired: "Ausgemustert" }[s] || s;
}
